import store from '@/vuex/store'
import UserApi from '@/api/user'
import { USER_PREFERENCE_REGISTRY } from '@/business-logic/user-preferences/userPreferenceRegistry'
import {
  findUserPreferenceDefinition,
  isUserPreferenceAvailable,
  isValidUserPreferenceValue,
  resolveUserPreferenceValue,
} from '@/business-logic/user-preferences/userPreferenceValues'

/**
 * The current user's value for a registered preference, or its registry default when the user has not
 * set it, the stored value is invalid, or its feature flag / plan gate is closed.
 * Returns undefined for a key that is not in the registry.
 */
export const getUserPreference = (key) => {
  const definition = findUserPreferenceDefinition(USER_PREFERENCE_REGISTRY, key)
  if (!definition) return undefined
  return resolveUserPreferenceValue(definition, store.state.currentUser?.preferences, store.getters)
}

const commitPreference = (key, value) => {
  store.commit('updateCurrentUser', {
    preferences: { ...store.state.currentUser.preferences, [key]: value },
  })
}

const pendingSaves = new Map()

const persistPreference = async (key, value) => {
  const { id: userId, preferences: previousPreferences } = store.state.currentUser
  const previousValue = previousPreferences?.[key]
  commitPreference(key, value)
  try {
    await UserApi.patchUserById(userId, { preferences: { [key]: value } })
  } catch (error) {
    commitPreference(key, previousValue)
    throw error
  }
}

/**
 * Saves one registered preference, updating the local user first and restoring the previous value
 * if the request fails (the error is rethrown). `null` resets the key so it reads as the default.
 * Saves of the same key run one after another, so a rollback never overwrites a later save.
 */
export const saveUserPreference = async (key, value) => {
  const definition = findUserPreferenceDefinition(USER_PREFERENCE_REGISTRY, key)
  if (!definition) throw new Error(`Unknown user preference "${key}"`)
  if (!isUserPreferenceAvailable(definition, store.getters)) throw new Error(`User preference "${key}" is not available`)
  if (value !== null && !isValidUserPreferenceValue(definition, value)) {
    throw new Error(`Invalid value for user preference "${key}"`)
  }

  const queued = (pendingSaves.get(key) || Promise.resolve()).catch(() => {}).then(() => persistPreference(key, value))
  pendingSaves.set(key, queued)
  return queued
}
